import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Flex,
  SimpleGrid,
  Stack,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useAuth } from 'contexts/AuthContext';
import { useUser } from 'contexts/UserContext';
import CInput from 'components/CInput';
import CRadio from 'components/CRadio';
import CButton from 'components/CButton';
import ImageUpload from 'components/ImageUpload';
import { isValidDOB } from 'helpers/helper';

const userRoles = [
  { value: "admin", label: "Admin" },
  { value: "receptionist", label: "Receptionist" },
  { value: "trainer", label: "Trainer" },
];

export default function EditProfile() {
  const { auth } = useAuth();
  const { getSingleUser, createUser, updateUser, } = useUser();
  const navigate = useNavigate();

  const textColor = useColorModeValue('secondaryGray.900', 'white');

  const [validated, setValidated] = useState(false);
  const [userDetail, setUserDetail] = useState({
    first_name: "",
    last_name: "",
    dob: "",
    role: "",
    image: "",
    is_web: true,
  });

  useEffect(() => {
    fetchSingleUser();

    return () => {
      setUserDetail({
        first_name: "",
        last_name: "",
        email: "",
        dob: "",
        role: "",
        image: "",
      })
    }
  }, [auth]);

  const fetchSingleUser = async () => {
    if (auth?.user.id) {
      const user = await getSingleUser(auth?.user.id)
      if (user) {
        setUserDetail(user);
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    if (!userDetail.email || !userDetail.first_name || !userDetail.last_name || !userDetail.dob || !userDetail.role || !isValidDOB(userDetail.dob, 16)) return;

    try {
      const formData = new FormData();
      formData.append('first_name', userDetail.first_name);
      formData.append('last_name', userDetail.last_name);
      formData.append('email', userDetail.email);
      formData.append('dob', userDetail.dob);
      formData.append('role', userDetail.role);
      formData.append('image', userDetail.image ? userDetail.image : '');
      const data = await updateUser(auth?.user.id, formData);
      if (!data.error) {
        navigate('/admin/dashboard/overview');
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      align="start"
      top="0"
      pt={{ sm: '125px', lg: '75px' }}
      position="relative"
      w="100%"
    >
      <Tabs w="100%">
        <TabPanels mt="24px" maxW="100%" w="100%" mx="auto">
          <TabPanel w="100%" p="0px" mx="auto">
            <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
              User Info
            </Text>
            <form onSubmit={handleSubmit}>
              <Flex direction="column" w="100%">
                <Stack direction="column" spacing="20px">
                  <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px" w="100%">
                    <CInput
                      label="First Name"
                      type="text"
                      value={userDetail.first_name}
                      onChange={(value) => setUserDetail({ ...userDetail, first_name: value })}
                      required={true}
                      validated={validated}
                      placeholder="e.g., Esthera"
                      w="100%"
                    />
                    <CInput
                      label="Last Name"
                      type="text"
                      value={userDetail.last_name}
                      onChange={(value) => setUserDetail({ ...userDetail, last_name: value })}
                      required={true}
                      validated={validated}
                      placeholder="e.g., Deo"
                      w="100%"
                    />
                    <CInput
                      label="Email"
                      type="text"
                      value={userDetail.email}
                      onChange={(value) => setUserDetail({ ...userDetail, email: value })}
                      required={true}
                      validated={validated}
                      placeholder="e.g., mail@sample.com"
                      w="100%"
                      disabled={Boolean(auth?.user.id)}
                    />
                    <CInput
                      label="Date of Birth"
                      type="date"
                      value={userDetail.dob}
                      onChange={(value) => setUserDetail({ ...userDetail, dob: value })}
                      required={true}
                      validated={validated}
                      w="100%"
                      max={new Date().toISOString().split("T")[0]}
                    />
                    <ImageUpload
                      label="Image"
                      existingImage={userDetail.image && userDetail.image instanceof File ? URL.createObjectURL(userDetail.image) : userDetail.image}
                      onImageSelect={(file) => setUserDetail({ ...userDetail, image: file })}
                    />
                    <CRadio
                      label="User Role"
                      options={userRoles}
                      value={userDetail.role}
                      onChange={(value) => setUserDetail({ ...userDetail, role: value })}
                      required={true}
                      isInvalid={validated && !userDetail.role}
                      errorMessage="Please select a user role."
                      disabled={Boolean(auth?.user.id)}
                    />
                  </SimpleGrid>
                </Stack>
                <Flex justify="flex-end" align="center" mt="44px" gap="3">
                  <Button
                    variant="darkBrand"
                    fontSize="sm"
                    borderRadius="10px"
                    w={{ base: '128px', md: '148px' }}
                    h="46px"
                    type="button"
                    bg="gray"
                    color="white"
                    _hover={{ bg: 'gray.600' }}
                    onClick={() => navigate('/admin/dashboard/overview')}
                  >
                    Cancel
                  </Button>
                  <CButton
                    w={{ base: '128px', md: '148px' }}
                    h="46px"
                    type="submit"
                  >
                    Submit
                  </CButton>
                </Flex>
              </Flex>
            </form>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Flex >
  );
}
